/* eslint-disable no-use-before-define */
import dayjs from "dayjs";
import { groupBy, isEmpty, map, sumBy } from "lodash";

const duration = require("dayjs/plugin/duration");

dayjs.extend(duration);

const MILISECONDS_DAY = 86400000;

// Filters Constant
export const THREE_MONTHS = "THREE_MONTHS";
export const ONE_MONTH = "ONE_MONTH";
export const TWO_WEEKS = "TWO_WEEKS";
export const THIS_MONTH = "THIS_MONTH";
export const THIS_WEEK = "THIS_WEEK";
export const LAST_MONTH = "LAST_MONTH";
export const LAST_WEEK = "LAST_WEEK";
export const LAST_30_DAYS = "LAST_30_DAYS";

// Actions
export const SET_PERIOD = "SET_PERIOD";
export const SET_CHANGE = "SET_CHANGE";
export const SET_TEAM = "SET_TEAM";
export const SET_GRAPH_TYPE = "SET_GRAPH_TYPE";

export const activityAnalysis = (data, range) => {
  const SENT = ["SENT", "DELIVERED"];
  const RECEIVED = ["RESPONDED"];
  const ENGAGED = ["ENGAGED"];
  const APPOINTMENT = ["APPOINTMENT"];
  const ACTIVE_ENGAGEMENTS = ["ACTIVE_ENGAGEMENTS"];
  const rangedData = filterByRange(data, range);

  const previousDate = substractDays(new Date().setHours(0, 0, 0, 0), range / 2);
  const previousData = filterByRange(data, range, previousDate);

  // actual
  const actualSent = countByStatus(rangedData)(SENT);
  const actualReceived = countByStatus(rangedData)(RECEIVED);
  const actualEnagaged = countByStatus(rangedData)(ENGAGED);
  const actualAppointment = countByStatus(rangedData)(APPOINTMENT);
  const actualActiveEngagement = countByStatus(rangedData)(ACTIVE_ENGAGEMENTS);

  const totalMessages = actualSent + actualReceived;

  // previous
  const previousSent = countByStatus(previousData)(SENT);
  const previousReceived = countByStatus(previousData)(RECEIVED);
  const previousEngaged = countByStatus(previousData)(ENGAGED);
  const previousAppointment = countByStatus(previousData)(APPOINTMENT);
  const previousTotalLeads = previousSent + previousReceived;

  const respRate =
    actualSent > 0 && actualReceived > 0
      ? Number((actualReceived / totalMessages) * 100).toFixed(2)
      : 0;

  const prevRespRate =
    previousSent > 0 && previousTotalLeads > 0 && previousReceived > 0
      ? Number((previousReceived / previousTotalLeads) * 100).toFixed(2)
      : 0;

  return [
    {
      title: "Total Messages",
      count: totalMessages,
      change: getChange(totalMessages, previousTotalLeads),
      previous: previousTotalLeads,
    },
    {
      title: "Total Messages Sent",
      count: actualSent,
      change: getChange(actualSent, previousSent),
      previous: previousSent,
    },
    {
      title: "Response Rate",
      count: respRate,
      change: getChange(respRate, prevRespRate),
      previous: prevRespRate,
      isPercentage: true,
    },
    {
      title: "Engagements",
      count: actualEnagaged,
      change: getChange(actualEnagaged, previousEngaged),
      previous: previousEngaged,
      compareType: true,
      isEngagements: false,
    },
    {
      title: "Appointments",
      count: actualAppointment,
      change: getChange(actualAppointment, previousAppointment),
      previous: previousAppointment,
      compareType: true,
      isEngagements: false,
    },
    {
      title: "Active Engagements",
      count: actualActiveEngagement,
      change: null,
      previous: null,
      compareType: false,
      isEngagements: true,
    },
  ];
};

export const responseByEngagement = data => {
  const newData = [...data];

  const summedData = map(groupBy(newData, "attempts"), (groupData, attempt) => {
    const name = getAttemptName(attempt);
    return { type: name, value: sumBy(groupData, "count") };
  });

  const summed = summedData.filter(el => el.type);

  if (!isEmpty(summed)) {
    return summed;
  }
  return [];
};

export const leadBySource = data => {
  const summed = map(groupBy(data, "source"), (groupData, idx) => {
    return { id: idx, summed: sumBy(groupData, "count") };
  });

  if (!isEmpty(summed)) {
    const graphdata = summed.map(field => {
      return {
        type: field.id,
        value: field.summed,
      };
    });
    return graphdata;
  }
  return [];
};

export const appointmentsAnalytics = data => {
  const localData = (data || []).map(el => ({
    ...el,
    previous: el.prevCount,
    change: el.growthRate.toFixed(0) || getChange(el.count, el.prevCount),
  }));

  return localData;
};

export const appointmentsBySalesPerson = data => {
  const summed = map(groupBy(data, "userName"), (groupData, key) => {
    return { id: key, summed: sumBy(groupData, "count") };
  });

  if (!isEmpty(summed)) {
    const graphdata = summed.map(field => {
      return {
        type: field.id,
        value: field.summed,
      };
    });
    return graphdata || [];
  }
  return [];
};

export const leadAnalysis = (data, range) => {
  const UNRESPONDED = ["SENT", "DELIVERED", "OPT_PUT"];
  const RESPONDED = ["RESPONDED"];
  const ENGAGED = ["ENGAGED"];

  const rangedData = filterByRange(data, range / 2);
  const previousDate = substractDays(new Date().setHours(0, 0, 0, 0), range / 2);
  const previousData = filterByRange(data, range / 2, previousDate);

  // actual
  const actualUnresponded = countByStatus(rangedData)(UNRESPONDED);
  const actualEngaged = countByStatus(rangedData)(ENGAGED);
  const actualResponded = countByStatus(rangedData)(RESPONDED);
  const totalLeads = countByStatus(rangedData)("N/A");

  // previous
  const previousTotalLeads = countByStatus(previousData)("N/A");

  return [
    {
      title: "Total New Leads",
      count: totalLeads,
      change: getChange(totalLeads, previousTotalLeads),
      previous: previousTotalLeads,
    },
    {
      title: "Responded/Unresponded",
      count: actualResponded,
      previous: actualUnresponded,
      compareType: true,
    },
    {
      title: "Single Response/Engagements",
      count: actualResponded - actualEngaged,
      previous: actualEngaged,
      compareType: true,
    },
  ];
};

export const filterByRange = data =>
  (data || []).map(item => {
    const localDate = item.date;

    return {
      ...item,
      date: localDate, // we manually set the time to zero because of possible time discrepancies
    };
  });

export const substractDays = (date, days) => {
  return zeroDate(date) - MILISECONDS_DAY * days;
};

const zeroDate = date => (date ? new Date(date) : new Date().setHours(0, 0, 0, 0));

export const getChange = (newValue, oldValue) => {
  const change = Number((((newValue - oldValue) / oldValue) * 100).toFixed(0));
  if (Number.isNaN(change)) return "-";
  if (change === Infinity) return "*";
  return change;
};

// filters the data and get the count based on the status
export const countByStatus = data => status => {
  if (data.length <= 0) return 0;

  return filterByStatus(data)(status).reduce((acc, curr) => acc + curr.count, 0);
};

export const filterByStatus = data => status => {
  if (data.length <= 0) return [];

  if (typeof status === "string") {
    status = [status];
  }

  return data.filter(kpi => status.indexOf(kpi.status) >= 0).flat();
};

export const getAttemptName = attempt => {
  let name = "";
  switch (attempt) {
    case "1":
      name = "1st Attempt";
      break;
    case "2":
      name = "2nd Attempt";
      break;
    case "3":
      name = "3rd Attempt";
      break;
    case "4":
      name = "4th Attempt";
      break;
    case "5":
      name = "5th Attempt";
      break;
    default:
      break;
  }
  return name;
};

const changeMenu = {
  THIS_MONTH: {
    label: "This Month",
    range: +dayjs().daysInMonth(),
  },
  LAST_MONTH: {
    label: "Last Month",
    range: +dayjs().subtract(1, "months").daysInMonth(),
  },
  LAST_WEEK: { label: "Last Week", range: 7 },
  THIS_WEEK: { label: "This Week", range: 7 },
};

export const periodsMenu = {
  THIS_MONTH: {
    label: "This Month",
    range: +dayjs().daysInMonth(),
  },
  LAST_MONTH: {
    label: "Last Month",
    range: +dayjs().subtract(1, "months").daysInMonth(),
  },
  LAST_WEEK: { label: "Last Week", range: 7 },
  THIS_WEEK: { label: "This Week", range: 7 },
};
export const parseSourceNames = text => {
  return text.replace(/^\d+-/, "");
};

export const DASHBOARD_SECTIONS = {
  BY_SOURCE: "BY_SOURCE",
  REVIEW_STATUS: "REVIEW_STATUS",
  REVIEW_REQUEST_ANALYSIS: "REVIEW_REQUEST_ANALYSIS",
  LEAD_ENGAGEMENT: "LEAD_ENGAGEMENT",
  RESPONSE_TIME: "RESPONSE_TIME",
  APPOINTMENT_BY_SALES_PERSON: "APPOINTMENT_BY_SALES_PERSON",
  APPOINTMENT_ANALYSIS: "APPOINTMENT_ANALYSIS",
  MESSAGE_SENT_RECIEVED_ENGAGEMENT: "MESSAGE_SENT_RECIEVED_ENGAGEMENT",
  ENGAGEMENT_ANALYSIS: "ENGAGEMENT_ANALYSIS",
  LEAD_STATS: "LEAD_STATS",
  MESSAGE_TYPE: "MESSAGE_TYPE",
};

export const convertMinutesToHour = minutes => {
  return Math.floor(minutes);
};

// Appointment / Opportunity view types
export const VIEW_TYPES = {
  APPOINTMENT: "APPOINTMENT",
  OPPORTUNITY: "OPPORTUNITY",
  MESSAGES: "MESSAGES",
  ACTIVITIES: "ACTIVITIES",
};

// Fields accepted by the dashboard analytics queries as `orderBy`.
// Shared across `getUserMessageTypeAnalysis`, `getDataBySource` (APPOINTMENT /
// LEAD_OPPORTUNITY / RESPONSE), `getMessagesBySalesperson`,
// `getOpportunityBySalesPerson`, `getAppointmentSalesperson`,
// `getLeadEngagementAnalysis`, `getUserMessageResponseTimeAnalysis`.
export const ORDER_BY_FIELDS = {
  // Common
  FULL_NAME: "full_name",
  BEST_USER_STATS: "best_user_stats",
  SOURCE_NAME: "sourceName",

  // getUserMessageTypeAnalysis — Message Type table columns
  TEXT_MESSAGES: "text_messages",
  IMAGE_MESSAGES: "image_messages",
  VIDEO_MESSAGES: "video_messages",
  DOC_MESSAGES: "doc_messages",
  AUDIO_MESSAGES: "audio_messages",

  // getDataBySource — APPOINTMENT columns + getAppointmentSalesperson
  SCHEDULED: "scheduled",
  RESCHEDULED: "rescheduled",
  SHOWED: "showed",
  CANCELLED: "cancelled",
  NO_SHOWED: "no_showed",
  CONFIRMED: "confirmed",
  SOLD: "sold",
  TOTAL_APPOINTMENTS: "total_appointments",

  // getDataBySource — RESPONSE columns shared
  OPPORTUNITIES: "opportunities",
  MESSAGES_SENT: "messages_sent",
  MESSAGES_RECEIVED: "messages_received",
  CALL_OUTBOUND: "call_outbound",
  CALL_INBOUND: "call_inbound",

  // getMessagesBySalesperson — Communication by Sales Person columns.
  // Each channel (Text / Email) is broken down into Otto + AI-Assisted +
  // AI-Accepted + AI-Modified + Manual + Received. AI-Assisted is the roll-up
  // of Accepted (AI text sent as-is) + Modified (AI draft edited then sent).
  TOTAL_MESSAGES: "total_messages",
  TEXT_OTTO_SENT: "text_otto_sent",
  TEXT_AI_ASSISTED_SENT: "text_ai_assisted_sent",
  TEXT_AI_ACCEPTED_SENT: "text_ai_accepted_sent",
  TEXT_AI_MODIFIED_SENT: "text_ai_modified_sent",
  TEXT_MANUAL_SENT: "text_manual_sent",
  TEXT_RECEIVED: "text_received",
  EMAIL_OTTO_SENT: "email_otto_sent",
  EMAIL_AI_ASSISTED_SENT: "email_ai_assisted_sent",
  EMAIL_AI_ACCEPTED_SENT: "email_ai_accepted_sent",
  EMAIL_AI_MODIFIED_SENT: "email_ai_modified_sent",
  EMAIL_MANUAL_SENT: "email_manual_sent",
  EMAIL_RECEIVED: "email_received",

  // getOpportunityBySalesPerson — Opportunity by Sales Person
  // Note: dynamic status columns send the literal `statusName` string
  // (e.g. "0 - Unqualified") as the `orderBy` value.
  TOTAL_OPPORTUNITIES: "total_opportunities",

  // getLeadEngagementAnalysis — Lead Engagement table columns
  UNCONTACTED: "uncontacted",
  NOT_RESPONDED: "not_responded",
  RESPONDED: "responded",
  NOT_ENGAGED: "not_engaged",
  ENGAGED: "engaged",
  WAITING_FOR_LEAD_REPLY: "waiting_for_lead_reply",
  NEEDS_YOUR_REPLY: "needs_your_reply",
  NO_RESPONSE_NEEDED: "no_response_needed",

  // getUserMessageResponseTimeAnalysis — Response Time table columns
  BEST_TIME: "best_time",
  WORST_TIME: "worst_time",
  AVERAGE_TIME: "average_time",
  MEDIAN_TIME: "median_time",

  // getActivitiesBySalesperson — Activity by Sales Person columns
  CREATED_COUNT: "created_count",
  ASSIGNED_COUNT: "assigned_count",
  UPCOMING: "upcoming",
  OVERDUE: "overdue",
  COMPLETED: "completed",
};

// View modes for "by Sales Person" metrics (sent as-is in the viewMode query variable)
export const VIEW_MODES = {
  WATERFALL: "waterfall",
  NORMAL: "normal",
};

export const VIEW_MODE_OPTIONS = [
  { label: "Waterfall", value: VIEW_MODES.WATERFALL },
  { label: "Normal", value: VIEW_MODES.NORMAL },
];

// Known acronyms that should stay uppercase after title-casing. The default
// pipeline lower-cases everything except the first letter, so "AI" comes back
// as "Ai" — this map patches it back. `\b` keeps the match scoped to whole
// words so compound names like "Aim" or "Aiden" stay untouched.
const TITLE_CASE_ACRONYMS = [["Ai", "AI"]];

export function convertToTitleCase(str) {
  const titled = str
    // Convert snake_case to camelCase
    .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
    // Add space before capital letters
    .replace(/([A-Z])/g, " $1")
    // Capitalize the first letter
    .replace(/^./, firstChar => firstChar.toUpperCase())
    // Replace underscores and hyphens with spaces
    .replace(/[_-]/g, " ")
    // Trim any leading or trailing spaces
    .trim();
  return TITLE_CASE_ACRONYMS.reduce(
    (acc, [from, to]) => acc.replace(new RegExp(`\\b${from}\\b`, "g"), to),
    titled
  );
}
